import { useEffect, useContext, useState } from 'react';
import { IntlProvider } from "react-intl";
import { useLocation, Link } from 'react-router-dom';
import { LanguageContext } from '../contexts/LanguageContext';
import url from '../constants/urlsPaths';
import './UserInfo.css';
import Preferences from '../generics/Preferences';
import Axios from 'axios';
import jspdf from "jspdf";
import html2canvas from 'html2canvas';

import enFlag from '../ressources/en.webp'; 
import frFlag from '../ressources/fr.webp';
import deFlag from '../ressources/de.webp';
import esFlag from '../ressources/es.webp';
import itFlag from '../ressources/it.webp';
import jaFlag from '../ressources/ja.svg';
import zhFlag from '../ressources/zh.svg';
import zhTWFlag from '../ressources/zh-tw.svg';

const flagMap = {
  en: enFlag,
  fr: frFlag,
  de: deFlag,
  es: esFlag,
  it: itFlag,
  ja: jaFlag,
  zh: zhFlag,
  'zh-TW': zhTWFlag,
  'zh-tw': zhTWFlag,
  'ja-JP': jaFlag,
};

const getFlagVisualStyle = (lang) => {
  const normalizedLang = String(lang || '').trim();
  if (normalizedLang === 'zh') {
    return {
      objectFit: 'contain',
      objectPosition: 'center center',
      backgroundColor: '#de2910'
    };
  }
  if (normalizedLang === 'zh-TW' || normalizedLang === 'zh-tw') {
    return {
      objectFit: 'contain',
      objectPosition: 'left center',
      backgroundColor: '#fe0000'
    };
  }
  return {
    objectFit: 'cover',
    objectPosition: 'center center',
    backgroundColor: 'transparent'
  };
};

const normalizeLocaleForMessages = (lang) => {
  const raw = String(lang || '').trim();
  if (!raw) return 'en';
  const lowered = raw.toLowerCase();

  if (lowered === 'ja-jp' || lowered === 'ja_jp') return 'ja';
  if (lowered === 'zh-tw' || lowered === 'zh_tw' || lowered === 'zh-hant' || lowered === 'zh-hk') return 'zh-TW';
  if (lowered === 'zh-cn' || lowered === 'zh_cn' || lowered === 'zh-hans') return 'zh';

  if (lowered === 'en' || lowered === 'fr' || lowered === 'de' || lowered === 'es' || lowered === 'it' || lowered === 'ja' || lowered === 'zh') {
    return lowered;
  }

  if (raw === 'zh-TW') return 'zh-TW';
  return raw;
};

const formatDateForLocale = (value, locale) => {
  if (!value) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      weekday: 'short',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      hour12: false,
      timeZone: 'UTC'
    }).format(parsed);
  } catch (e) {
    return value;
  }
};


function LocalizedTranslatePageCharts(props) {
  const [messages, setMessages] = useState({});
  const locale = normalizeLocaleForMessages(props.transalateToLanguage || "en");
  const localizedDatePassed = formatDateForLocale(props.datePassed, locale);


useEffect(() => {
  const cacheBuster = Date.now();
  fetch(`/lang/${locale}.json?cb=${cacheBuster}`)
    .then(res => res.json())
    .then(setMessages)
    .catch(() => setMessages({}));
}, [locale]);

  if (Object.keys(messages).length === 0) return null; // or a loader

  const { TranslatePageCharts } = props.PageChartsComponents;
  return (
    <IntlProvider locale={locale} messages={messages}>
      <TranslatePageCharts {...props} datePassed={localizedDatePassed} />
    </IntlProvider>
  );
}

function LocalizedEditPageCharts(props) {
  const [messages, setMessages] = useState({});
  const locale = normalizeLocaleForMessages(props.locale || "en");
  const localizedDatePassed = formatDateForLocale(props.datePassed, locale);

  useEffect(() => {
    const cacheBuster = Date.now();
    fetch(`/lang/${locale}.json?cb=${cacheBuster}`)
      .then(res => res.json())
      .then(setMessages)
      .catch(() => setMessages({}));
  }, [locale]);

  if (Object.keys(messages).length === 0) return null; // or a loader

  const { PageCharts } = props.PageChartsComponents;
  return (
    <IntlProvider locale={locale} messages={messages}>
      <PageCharts
        {...props}
        datePassed={localizedDatePassed}
        currentLanguage={locale}
      />
    </IntlProvider>
  );
}

// Dynamically choose PageCharts or EditPageCharts based on URL
function getPageChartsComponent(pathname, editLanguage) {
  return { PageCharts: require('../generics/PageChartsAndAttitudes').default };
}

const PDFLogic = async (userCode, email, language, action = "download") => {
  const pdfBtnLoader = document.getElementById("loader");
  const pdfBtntext = document.getElementById("pdf-btn-text");
  if (pdfBtnLoader) pdfBtnLoader.style.display = "block";
  if (pdfBtntext) pdfBtntext.style.display = "none";
  let doc = new jspdf('p', 'pt', [842, 595]);
  const pages = document.getElementsByClassName("PageCharts-chart");
  let scale = 1.66;
  for (let i = 0; i < pages.length; i++) {
    await new Promise(function(resolve) {
      html2canvas(pages[i], {scale: scale}).then(function(canvas) {
        var img = canvas.toDataURL("image/jpeg", 0.8);
        doc.addImage(img, 'JPEG', 0, 0, 595, 842);
        if ((i + 1) === pages.length) {
          if (action === "download" || action === "downloadAndSend") {
            doc.save('Scope-Report-' + userCode + '.pdf', { compress: true });
          }
          if ((action === "send" || action === "downloadAndSend") && email) {
            // Send PDF to server for emailing
            const blob = doc.output('blob');
            const formData = new FormData();
            formData.append('pdf', blob, 'Scope-Report-' + userCode + '.pdf');
            formData.append('email', email);
            formData.append('language', language);
            Axios.post(url.sendEmailPdf, formData, {
                headers: {
                  "x-access-token": (() => {
                    const token = sessionStorage.getItem("token");
                    if (!token || token === "null" || token === "undefined") {
                      return null;
                    }
                    try {
                      return JSON.parse(token);
                    } catch (e) {
                      return null;
                    }
                  })()
                }
              })
              .then(res => {
                alert(res.data.success ? "Email sent!" : "Failed to send email.");
              })
              .catch(() => {
                alert("Failed to send email.");
              });
          }
        } else {
          doc.addPage();
        }
        resolve();
      });
    });
  }
  setTimeout(function() {
    if (pdfBtnLoader) pdfBtnLoader.style.display = "none";
    if (pdfBtntext) pdfBtntext.style.display = "block";
  }, 3000);
  return true;
};

// Check whether a PDF for this user already exists on the server
const checkPdfExists = async (userKey, loc, auth) => {
  try {
    const token = auth && auth !== "null" && auth !== "undefined" ? auth : null;
    const res = await Axios.get(
      url.checkPdfExists + `?key=${userKey}`,
      {
        headers: {
          "x-access-token": token
        }
      }
    );
    return res.data && res.data.exists;
  } catch (err) {
    console.error("Error checking PDF existence:", err);
    return false;
  }
};

export default function UserInfo() {
  const location = useLocation();
  const auth = (() => {
    if (location.state && location.state.auth) return location.state.auth;
    const t = sessionStorage.getItem('token');
    if (!t || t === 'null' || t === 'undefined') return null;
    try { return JSON.parse(t); } catch (e) { return t; }
  })();
  console.log("UserInfo location state:", location);
  const { locale } = useContext(LanguageContext);
  const hideBack = location.pathname.includes('userInfo') && (location.state && (location.state.email === "" || location.state.email == null));
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState(location.state.email || "");
  const PageChartsComponents = getPageChartsComponent(location.pathname, location.state.editLanguage);
  const [scale, setScale] = useState(1);
  const [marginLeft, setMarginLeft] = useState(0);
  const [messagesData, setMessagesData] = useState({});

  const translateFromLanguage = location.state?.from || null; 
  const translateToLanguage = location.state?.to || null; 

  const effectiveLocale = PageChartsComponents.forceLocale || locale;
  const localizedDateSurveyPassed = formatDateForLocale(location.state.dateSurveyPassed, effectiveLocale);

  const updateMessage = (key, newValue) => {
    console.log("Updating message:", key, newValue);
    setMessagesData(prev => {
      const updated = { ...prev };
      if (updated[key]) {
        updated[key].defaultMessage = newValue;
      } else {
        updated[key] = { id: key, defaultMessage: newValue };
      }
      return updated;
    });
};

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1500) {
        setScale(0.9);
        setMarginLeft(-65);
      } else if (window.innerWidth >= 1200) {
        setScale(0.85);
        setMarginLeft(-100);
      } else {
        setScale(0.65);
        setMarginLeft(-260);
      }
    }
    handleResize(); // Set initial values
    window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleDownloadClick = () => {
    setShowPopup(true);
  };



  return (
    <div id="UserInfo" className={location.pathname.endsWith('/translate') ? "user-info-inline" : ""}>
      <div onClick={handleDownloadClick} className="download-pdf-btn">
        <div id="loader" className="loader-btn">
          <div className="loader">
            <div className="inner one"></div>
            <div className="inner two"></div>
            <div className="inner three"></div>
          </div>
        </div>
        <div id="pdf-btn-text" className="pdf-btn-text">PDF Options</div>
      </div>

      {showPopup && (
        <div className="pdf-popup-overlay">
          <div className="pdf-popup">
            <h3>PDF Options</h3>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ width: "100%", marginBottom: "10px" }}
              placeholder="Recipient email"
            />
            <div style={{ marginBottom: "10px" }}>
              <button
                className="popup-btn"
                onClick={async () => {
                  setShowPopup(false);
                  const exists = await checkPdfExists(location.state.code, location, auth);
                    if (exists) {
                    // ask server to send the stored PDF
                    Axios.post(url.sendEmailPdf, { code: location.state.code, email, language: location.state.language }, {
                      headers: {
                        "x-access-token": (() => {
                          if (auth && auth !== 'null' && auth !== 'undefined') return auth;
                          const t = sessionStorage.getItem('token');
                          if (!t || t === 'null' || t === 'undefined') return null;
                          try { return JSON.parse(t); } catch (e) { return t; }
                        })()
                      }
                    })
                      .then(res => alert(res.data.success ? "Email sent using existing PDF!" : "Failed to send email."))
                      .catch(() => alert("Failed to send email."));
                  } else {
                    // fallback to client-side generation and send
                    PDFLogic(location.state.code, email, location.state.language, "send");
                  }
                }}
              >
                Send PDF by Email
              </button>
              <button
                className="popup-btn"
                onClick={async () => {
                  setShowPopup(false);
                  const exists = await checkPdfExists(location.state.code, location, auth);
                  if (exists) {
                    // download from server
                    Axios.get(`${url.downloadPdf}?key=${location.state.code}`, { responseType: 'blob', headers: {
                      "x-access-token": (() => {
                        if (auth && auth !== 'null' && auth !== 'undefined') return auth;
                        const t = sessionStorage.getItem('token');
                        if (!t || t === 'null' || t === 'undefined') return null;
                        try { return JSON.parse(t); } catch (e) { return t; }
                      })()
                    } })
                      .then(res => {
                        const urlBlob = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = urlBlob;
                        link.setAttribute('download', `Scope-Report-${location.state.code}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      })
                      .catch(() => alert('Failed to download PDF.'));
                  } else {
                    PDFLogic(location.state.code, null, location.state.language, "download");
                  }
                }}
              >
                Download PDF
              </button>
              <button
                className="popup-btn"
                onClick={async () => {
                  setShowPopup(false);
                  const exists = await checkPdfExists(location.state.code, location, auth);
                  if (exists) {
                    // open/download and request email send with auth header
                    const downloadUrl = `${url.downloadPdf}?code=${location.state.code}`;
                    // Open in new tab with auth header can't be set; rely on backend token check when API called below
                    window.open(downloadUrl, '_blank');
                    Axios.post(url.sendEmailPdf, { code: location.state.code, email, language: location.state.language }, {
                      headers: {
                        "x-access-token": (() => {
                          if (auth && auth !== 'null' && auth !== 'undefined') return auth;
                          const t = sessionStorage.getItem('token');
                          if (!t || t === 'null' || t === 'undefined') return null;
                          try { return JSON.parse(t); } catch (e) { return t; }
                        })()
                      }
                    })
                      .then(res => alert(res.data.success ? "Email sent using existing PDF!" : "Failed to send email."))
                      .catch(() => alert("Failed to send email."));
                  } else {
                    await PDFLogic(location.state.code, email, location.state.language, "downloadAndSend");
                  }
                }}
              >
                Download & Send PDF
              </button>
              <button className="popup-btn" onClick={() => setShowPopup(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <Preferences auth={auth} />
      <div id="UserInfo-header">
        {!hideBack && (
          <div id="UserInfo-header-backBtn">
            <Link to='/manager' className="UserInfo-back-btn">Back</Link>
          </div>
        )}
        <div id="UserInfo-header-content">
          <p><b>Code : </b>{location.state.code}</p>
          {email && <p><b>Email : </b>{email}</p>}
          <p><b>Date survey passed : </b>{location.state.dateSurveyPassed}</p>
        </div>
      </div>
      <div className={
              location.pathname.endsWith('/translate')
                ? "translate-side-by-side"
                : ""
            }>
       {location.pathname.endsWith('/translate') && 
            <div className="flags-box">
              <img
                src={flagMap[translateFromLanguage] || frFlag}
                alt="Flag 1"
                className="flag-from"
                style={getFlagVisualStyle(translateFromLanguage)}
              />
              <img
                src={flagMap[translateToLanguage] || enFlag}
                alt="Flag 2"
                className="flag-to"
                style={getFlagVisualStyle(translateToLanguage)}
              />
            </div>
          }
        <div className="UserInfo-content"
          style={location.pathname.endsWith('/translate')
                ? {
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          } : {}}
        >

        {PageChartsComponents.PageCharts && location.pathname.endsWith('/edit') && PageChartsComponents.forceLocale ? (
    <LocalizedEditPageCharts
      PageChartsComponents={PageChartsComponents}
      askedfor='admin'
      dataForGraphs={location.state.results}
      locale={PageChartsComponents.forceLocale}
      email={location.state.email}
      code={location.state.code}
      datePassed={location.state.dateSurveyPassed}
      timeTaken={location.state.testDuration}
      userLanguage={location.state.language}
      location={location}
      messagesData={messagesData}
      updateMessage={updateMessage}
    />
  ) : location.pathname.endsWith('/translate') ? (
    <LocalizedEditPageCharts
      PageChartsComponents={PageChartsComponents}
      askedfor='admin'
      dataForGraphs={location.state.results}
      locale={translateFromLanguage || 'fr'} // <-- use from-language here
      email={location.state.email}
      code={location.state.code}
      datePassed={location.state.dateSurveyPassed}
      timeTaken={location.state.testDuration}
      userLanguage={location.state.language}
      location={location}
      messagesData={messagesData}
      updateMessage={updateMessage}
    />
  ) : (
    <PageChartsComponents.PageCharts
      askedfor='admin'
      dataForGraphs={location.state.results}
      currentLanguage={effectiveLocale}
      localeDisplay={effectiveLocale}
      email={location.state.email}
      code={location.state.code}
      datePassed={localizedDateSurveyPassed}
      timeTaken={location.state.testDuration}
      userLanguage={location.state.language}
      location={location}
    />
  )}
</div>
        {location.pathname.endsWith('/translate') && (
          <div
            className="UserInfo-content translate-right"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              marginLeft: `${marginLeft}px`
            }}
          >
            <LocalizedTranslatePageCharts
                PageChartsComponents={PageChartsComponents}
                askedfor='admin'
                dataForGraphs={location.state.results}
                currentLanguage={locale}
                email={location.state.email}
                code={location.state.code}
                datePassed={location.state.dateSurveyPassed}
                timeTaken={location.state.testDuration}
                userLanguage={location.state.language}
                location={location}
                transalateToLanguage={translateToLanguage}
                messagesData={messagesData}
                updateMessage={updateMessage}
              />
            </div>
        )}
      </div>
    </div>
  );
}